import {
  FileFolderItem,
  isModelVisible,
  isPointCloudVisible,
  isTrajectoryVisible,
  MissionDetailsSelection,
  RunDetailsSelection,
  useDataContext,
} from '@mobile-mapping/providers';
import { LoadState } from '@trimble/data-loading';
import { t } from '@trimble/i18n';
import { sortItems } from '@trimble/ui-components/solidjs';
import { Empty } from '@tcweb/connect-ui';
import { JSX, Show, createMemo } from 'solid-js';
import { useProjectDataPanel } from '@trimble/project-data-panel-ui';
import { SharedTrajectoryTreeContainer } from './SharedTrajectoryTreeContainer';
import { SharedPointCloudsTreeContainer } from './SharedPointCloudsTreeContainer';
import { SharedModelsTreeContainer } from './SharedModelsTreeContainer';
import { SharedMissionTreeContainer } from './SharedMissionTreeContainer';
import {
  buildMissionNodes,
  buildModelNodes,
  buildPointCloudNodes,
  buildTrajectoryNodes,
  MissionTreeNodeType,
  MODEL_ROOT_NODE_ID,
  POINT_CLOUD_ROOT_NODE_ID,
  TRAJECTORY_ROOT_NODE_ID,
} from '../project-data-tree-nodes';
import {
  countRootNodes,
  filterProjectDataNodes,
} from '../project-data-filtering';
import { UnreachableCaseError } from 'ts-essentials';

type ChildrenLoadStatus = 'pending' | 'success';

const getChildrenLoadStatus = (state: LoadState): ChildrenLoadStatus =>
  state === LoadState.LoadComplete ? 'success' : 'pending';

export interface SharedProjectDataTreesProps {
  onModelDetailsSelect?(model: FileFolderItem): void;
  onRunDetailsSelect?(run: RunDetailsSelection): void;
  onMissionDetailsSelect?(mission: MissionDetailsSelection): void;
}

export const SharedProjectDataTrees = (
  props: SharedProjectDataTreesProps
): JSX.Element => {
  const { dataState } = useDataContext();
  const { filterText, filterMode } = useProjectDataPanel();

  const trajectoryNodes = createMemo(() =>
    dataState.trajectory.state === LoadState.LoadComplete
      ? buildTrajectoryNodes(Object.values(dataState.trajectory.items))
      : []
  );

  const pointCloudNodes = createMemo(() =>
    dataState.pointCloud.state === LoadState.LoadComplete
      ? buildPointCloudNodes(Object.values(dataState.pointCloud.items))
      : []
  );

  const modelNodes = createMemo(() =>
    dataState.tcFileFolder.state === LoadState.LoadComplete
      ? buildModelNodes(Object.values(dataState.tcFileFolder.items))
      : []
  );

  const missionNodes = createMemo(() =>
    dataState.mission.state === LoadState.LoadComplete
      ? buildMissionNodes(Object.values(dataState.mission.items))
      : []
  );

  const filteredTrajectoryNodes = createMemo(() =>
    filterProjectDataNodes(
      trajectoryNodes(),
      filterMode(),
      filterText(),
      (node) =>
        node.id !== TRAJECTORY_ROOT_NODE_ID &&
        isTrajectoryVisible(dataState, node.id)
    )
  );

  const filteredPointCloudNodes = createMemo(() =>
    filterProjectDataNodes(
      pointCloudNodes(),
      filterMode(),
      filterText(),
      (node) =>
        node.id !== POINT_CLOUD_ROOT_NODE_ID &&
        isPointCloudVisible(dataState, node.id)
    )
  );

  const filteredModelNodes = createMemo(() =>
    filterProjectDataNodes(
      modelNodes(),
      filterMode(),
      filterText(),
      (node) =>
        node.id !== MODEL_ROOT_NODE_ID && isModelVisible(dataState, node.id)
    )
  );

  const filteredMissionNodes = createMemo(() =>
    filterProjectDataNodes(
      missionNodes(),
      filterMode(),
      filterText(),
      (node) => {
        const type = node.data?.type;
        switch (type) {
          case MissionTreeNodeType.Run:
            return isPointCloudVisible(dataState, node.id);
          case MissionTreeNodeType.Path:
            return (
              isTrajectoryVisible(dataState, node.id) ||
              (node.data?.mission?.runs.some((run) =>
                isPointCloudVisible(dataState, run.id)
              ) ??
                false)
            );
          case MissionTreeNodeType.Mission:
          case MissionTreeNodeType.Runs:
          case undefined:
            return false;
          default:
            throw new UnreachableCaseError(type);
        }
      }
    )
  );

  const areAllFilteredTreesEmpty = createMemo(() => {
    return (
      countRootNodes(filteredTrajectoryNodes().nodes) === 0 &&
      countRootNodes(filteredPointCloudNodes().nodes) === 0 &&
      countRootNodes(filteredModelNodes().nodes) === 0 &&
      countRootNodes(filteredMissionNodes().nodes) === 0
    );
  });

  const filterKey = createMemo(() => `${filterMode()}:${filterText()}`);

  const hasFiltersApplied = createMemo(() => {
    return filterMode() !== 'all' || filterText().trim().length > 0;
  });

  const trajectoryControl = (
    <SharedTrajectoryTreeContainer
      nodes={filteredTrajectoryNodes().nodes}
      childrenLoadStatus={getChildrenLoadStatus(dataState.trajectory.state)}
      defaultExpandIds={filteredTrajectoryNodes().expandIds}
      filterKey={`${filterKey()}:${filteredTrajectoryNodes().expandIds.join(',')}`}
      highlightText={filterText()}
    />
  );

  const pointCloudControl = (
    <SharedPointCloudsTreeContainer
      nodes={filteredPointCloudNodes().nodes}
      childrenLoadStatus={getChildrenLoadStatus(dataState.pointCloud.state)}
      defaultExpandIds={filteredPointCloudNodes().expandIds}
      filterKey={`${filterKey()}:${filteredPointCloudNodes().expandIds.join(',')}`}
      highlightText={filterText()}
    />
  );

  const modelControl = (
    <SharedModelsTreeContainer
      nodes={filteredModelNodes().nodes}
      childrenLoadStatus={getChildrenLoadStatus(dataState.tcFileFolder.state)}
      defaultExpandIds={filteredModelNodes().expandIds}
      filterKey={`${filterKey()}:${filteredModelNodes().expandIds.join(',')}`}
      highlightText={filterText()}
      onModelDetailsSelect={props.onModelDetailsSelect}
    />
  );
  const missionControl = (
    <SharedMissionTreeContainer
      nodes={filteredMissionNodes().nodes}
      childrenLoadStatus={getChildrenLoadStatus(dataState.mission.state)}
      defaultExpandIds={
        hasFiltersApplied() ? filteredMissionNodes().expandIds : undefined
      }
      filterKey={`${filterKey()}:${filteredMissionNodes().expandIds.join(',')}`}
      highlightText={filterText()}
      onRunDetailsSelect={props.onRunDetailsSelect}
      onMissionDetailsSelect={props.onMissionDetailsSelect}
    />
  );

  interface ControlMap {
    control: JSX.Element;
    label: string;
  }

  const treeControls: ControlMap[] = [
    {
      control: trajectoryControl,
      label: t('mobileMapping.projectData.trajectories'),
    },
    {
      control: pointCloudControl,
      label: t('mobileMapping.projectData.pointClouds'),
    },
    {
      control: modelControl,
      label: t('mobileMapping.projectData.models'),
    },
    {
      control: missionControl,
      label: t('mobileMapping.missions.missions'),
    },
  ];

  const sortedTreeControls = createMemo(() =>
    [...treeControls]
      .sort((a, b) => sortItems(a, b, 'label', 'asc'))
      .map(({ control }) => control)
  );

  return (
    <Show
      when={!areAllFilteredTreesEmpty()}
      fallback={
        <Empty
          icon="tc-icon-folder"
          title={t('mobileMapping.projectData.noData.title')}
          message={
            hasFiltersApplied()
              ? t('mobileMapping.projectData.noData.messageWithFilters')
              : t('mobileMapping.projectData.noData.message')
          }
          class="flex-1 w-100"
          data-testid="project-data-no-data-found"
          isSmallContainer={true}
        />
      }
    >
      {sortedTreeControls()}
    </Show>
  );
};
